import React from 'react';
import { Box, Container, Typography, styled } from '@mui/material';

const Section = styled(Box)(({ theme }) => ({
    position: 'relative',
    paddingTop: theme.spacing(6.5),
    paddingBottom: theme.spacing(6.5),
    background: 'linear-gradient(180deg, #FFFFFF 0%, #F7F9FF 45%, #FFFFFF 100%)',
    overflow: 'hidden',
    '--ink': '#070B16',
    '--muted': '#475569',
    '--muted2': '#64748B',
    '--line': 'rgba(15, 23, 42, 0.10)',
    '--glass': 'rgba(255, 255, 255, 0.76)',
    '--glass2': 'rgba(248, 250, 255, 0.56)',
    '--shadow': '0 30px 80px rgba(2, 6, 23, 0.08), 0 10px 30px rgba(0, 102, 255, 0.06)',
    '--accent': '#0066FF',
    '--accent2': '#6366F1',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
            'radial-gradient(ellipse 40% 28% at 16% 20%, rgba(99, 102, 241, 0.09) 0%, transparent 70%), radial-gradient(ellipse 48% 34% at 86% 64%, rgba(0, 102, 255, 0.09) 0%, transparent 70%), linear-gradient(rgba(15, 23, 42, 0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(15, 23, 42, 0.04) 1px, transparent 1px)',
        backgroundSize: 'auto, auto, 44px 44px, 44px 44px',
        backgroundPosition: '0 0, 0 0, -6px -6px, -6px -6px',
        pointerEvents: 'none',
    },
    [theme.breakpoints.down('md')]: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
    },
    [theme.breakpoints.down('sm')]: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
    },
}));

const Wrap = styled(Box)({
    position: 'relative',
    zIndex: 1,
    maxWidth: '1040px',
    margin: '0 auto',
});

const Layout = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
    gap: '18px',
    alignItems: 'stretch',
    '@media (max-width: 968px)': {
        gap: '14px',
    },
});

const HeaderCard = styled(Box)({
    position: 'relative',
    // borderRadius: '24px',
    // padding: '28px 28px',
    // background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.78) 0%, rgba(248, 250, 255, 0.60) 100%)',
    // border: '1px solid rgba(15, 23, 42, 0.10)',
    // boxShadow: '0 22px 60px rgba(2, 6, 23, 0.06), 0 10px 26px rgba(0, 102, 255, 0.05)',
    // backdropFilter: 'blur(14px)',
    // overflow: 'hidden',
    // '&::before': {
    //     content: '""',
    //     position: 'absolute',
    //     inset: 0,
    //     background:
    //         'radial-gradient(ellipse 55% 45% at 18% 30%, rgba(0, 102, 255, 0.14) 0%, transparent 62%), radial-gradient(ellipse 55% 45% at 88% 20%, rgba(99, 102, 241, 0.12) 0%, transparent 62%)',
    //     opacity: 0.75,
    //     pointerEvents: 'none',
    // },
    '@media (max-width: 600px)': {
        padding: '22px 20px',
        borderRadius: '18px',
    },
});

const HeaderInner = styled(Box)({
    position: 'relative',
    zIndex: 1,
    maxWidth: '880px',
    margin: '0 auto',
    textAlign: 'center',
});

const Kicker = styled(Typography)(({ theme }) => ({
    display: 'inline-block',
    fontSize: '0.6875rem',
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#0066FF',
    backgroundColor: 'rgba(0, 102, 255, 0.1)',
    padding: theme.spacing(0.75, 1.75),
    borderRadius: 999,
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        fontSize: '0.75rem',
        padding: theme.spacing(0.75, 2.25),
    },
}));

const Title = styled(Typography)(({ theme }) => ({
    fontSize: '1.75rem',
    fontWeight: 700,
    lineHeight: 1.22,
    letterSpacing: '-0.03em',
    color: '#0F172A',
    margin: 0,
    [theme.breakpoints.up('sm')]: {
        fontSize: '2.125rem',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '2.5rem',
    },
    [theme.breakpoints.up('lg')]: {
        fontSize: '2.75rem',
    },
}));

const Sub = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(2),
    fontSize: '1rem',
    lineHeight: 1.75,
    fontWeight: 400,
    color: 'rgba(15, 23, 42, 0.82)',
    [theme.breakpoints.up('sm')]: {
        fontSize: '1.0625rem',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '1.125rem',
    },
}));

const Cards = styled(Box)({
    display: 'grid',
    gap: '16px',
});

const Card = styled(Box)({
    position: 'relative',
    borderRadius: '24px',
    padding: '26px 26px',
    background:
        'linear-gradient(180deg, var(--glass) 0%, rgba(255, 255, 255, 0.9) 100%)',
    border: '1px solid rgba(15, 23, 42, 0.10)',
    boxShadow: 'var(--shadow)',
    backdropFilter: 'blur(14px)',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    transition: 'transform 220ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 220ms cubic-bezier(0.4, 0, 0.2, 1), border-color 220ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background:
            'radial-gradient(ellipse 55% 45% at 12% 12%, rgba(0, 102, 255, 0.16) 0%, transparent 55%), radial-gradient(ellipse 55% 45% at 88% 60%, rgba(99, 102, 241, 0.14) 0%, transparent 60%)',
        opacity: 0.7,
        pointerEvents: 'none',
    },
    '&::after': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        width: '3px',
        height: '100%',
        background: 'linear-gradient(180deg, var(--accent) 0%, var(--accent2) 100%)',
        opacity: 0.9,
    },
    '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow:
            '0 36px 90px rgba(2, 6, 23, 0.10), 0 14px 36px rgba(0, 102, 255, 0.08)',
        borderColor: 'rgba(0, 102, 255, 0.22)',
    },
    '@media (max-width: 600px)': {
        padding: '22px 20px',
        borderRadius: '18px',
    },
});

const CardInner = styled(Box)({
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
});

const PersonRow = styled(Box)({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    marginBottom: '14px',
});

const AvatarImg = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center top',
    display: 'block',
});

const Avatar = styled(Box)({
    width: '58px',
    height: '58px',
    borderRadius: '18px',
    overflow: 'hidden',
    background:
        'linear-gradient(135deg, rgba(0, 102, 255, 0.26) 0%, rgba(99, 102, 241, 0.24) 100%)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    boxShadow: '0 14px 30px rgba(0, 102, 255, 0.14)',
    flex: '0 0 auto',
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: '-1px',
        borderRadius: '16px',
        padding: '1px',
        background: 'linear-gradient(135deg, rgba(0, 102, 255, 0.35), rgba(99, 102, 241, 0.28))',
        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
        WebkitMaskComposite: 'xor',
        maskComposite: 'exclude',
        opacity: 0.8,
        pointerEvents: 'none',
    },
});

const Name = styled(Typography)({
    fontSize: '18px',
    fontWeight: 700,
    letterSpacing: '-0.02em',
    color: '#0F172A',
    lineHeight: 1.25,
});

const Role = styled(Typography)({
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(0, 102, 255, 0.95)',
    marginTop: '3px',
});

const Body = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    lineHeight: 1.75,
    fontWeight: 400,
    color: '#0A0A0A',
    margin: 0,
    textAlign: 'justify',
    [theme.breakpoints.up('sm')]: {
        fontSize: '1.0625rem',
    },
    [theme.breakpoints.up('md')]: {
        fontSize: '1.125rem',
    },
}));

const QuoteRule = styled(Box)({
    marginTop: 'auto',
    paddingTop: '18px',
    height: '1px',
    width: '100%',
    background:
        'linear-gradient(90deg, rgba(0, 102, 255, 0.0), rgba(0, 102, 255, 0.26), rgba(99, 102, 241, 0.0))',
});

const LEADER_PHOTOS = {
    karthik: '/images/karthik.png',
    shubha: '/images/shubha.png',
    deeraj: '/images/deeraj.png',
};

const SeenProblemUpCloseSection = () => {
    return (
        <Section component="section" aria-labelledby="company-seen-title">
            <Container maxWidth="lg">
                <Wrap>
                    <Layout>
                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <HeaderCard>
                                <HeaderInner>
                                    <Kicker component="p">Leadership</Kicker>
                                    <Title variant="h2" id="company-seen-title">
                                        Seen this problem up close
                                    </Title>
                                    <Sub component="p">
                                        Built by people who've watched home healthcare operations run on informal
                                        systems - and designed meDZhub to make verification and compliance part of the
                                        workflow, not an added burden.
                                    </Sub>
                                </HeaderInner>
                            </HeaderCard>
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', md: '1 / span 6' }, display: 'flex' }}>
                            <Card>
                                <CardInner>
                                    <PersonRow>
                                        <Avatar aria-hidden>
                                            <AvatarImg
                                                src={LEADER_PHOTOS.karthik}
                                                alt=""
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </Avatar>
                                        <Box>
                                            <Name component="h3">Karthik Rajashekaran</Name>
                                            <Role component="p">CEO</Role>
                                        </Box>
                                    </PersonRow>
                                    <Body component="p">
                                    Managing Director of Dhruthzuci Technology Solutions. Over 15 years building software products, including 8 years in the startup space as part of the founding team at a Series B SaaS company. 
                                    He takes a focused approach. In healthcare, domain knowledge and long-term trust matter more than moving fast across too many areas.
                                    <br />
                                    He grew up in a family that ran clinics, pharmacies, and care services in rehabilitation, nursing, and home care. 
                                    These were operations that worked largely outside the hospital system. He watched these teams manage their field operations on WhatsApp, ledgers, and phone calls for years. 
                                    When he looked at the broader market, it was the same picture everywhere. No software had been built for how these operations actually ran.
                                    <br />
                                    He built meDZhub to solve what had gone unsolved for decades.
                                    </Body>
                                    <QuoteRule />
                                </CardInner>
                            </Card>
                        </Box>

                        <Box sx={{ gridColumn: { xs: '1 / -1', md: '7 / span 6' }, display: 'flex' }}>
                            <Card>
                                <CardInner>
                                    <PersonRow>
                                        <Avatar aria-hidden>
                                            <AvatarImg
                                                src={LEADER_PHOTOS.shubha}
                                                alt=""
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </Avatar>
                                        <Box>
                                            <Name component="h3">Shubha G J</Name>
                                            <Role component="p">Chief Product Officer</Role>
                                        </Box>
                                    </PersonRow>
                                    <Body component="p">
                                        Experienced in building mobile and web applications that are secure and scalable, she has led product strategy from early concept through execution across complex, real-world deployments. 
                                        As a Product Owner working across tier-3 India, she saw healthcare operations running on informal systems with zero documentation proof. Setups that worked locally but could not scale, 
                                        could not prove outcomes, and could not access institutional support because compliance tools were built for enterprises with legal teams, not small providers. 
                                        She built meDZhub's compliance architecture to sit inside operational workflows so centres become audit-ready by design.
                                    </Body>
                                    <QuoteRule />
                                </CardInner>
                            </Card>
                        </Box>

                        <Box sx={{ gridColumn: '1 / -1' }}>
                            <Card>
                                <CardInner>
                                    <PersonRow>
                                        <Avatar aria-hidden>
                                            <AvatarImg
                                                src={LEADER_PHOTOS.deeraj}
                                                alt=""
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        </Avatar>
                                        <Box>
                                            <Name component="h3">Dr. Dheeraj A</Name>
                                            <Role component="p">Clinical Advisor</Role>
                                        </Box>
                                    </PersonRow>
                                    <Body component="p">
                                        Head of Department, Physical Medicine and Rehabilitation at Manipal Hospital. 15+ years of clinical experience in PMR.
                                        Through his work in post-discharge care, he saw the same gap consistently: patients improved with therapy, but outcomes from home-based care went undocumented, unmeasured, and unverifiable. 
                                        Without structured records, insurance claims were rejected and hospitals had no visibility into what happened after discharge.
                                        <br />
                                        He partnered with meDZhub to build what clinic software never addressed: real-time verification of care delivered outside hospital settings, 
                                        documentation enforced at point of care, and outcome visibility for clinicians and hospitals managing patients through home rehabilitation.
                                    </Body>
                                </CardInner>
                            </Card>
                        </Box>
                    </Layout>
                </Wrap>
            </Container>
        </Section>
    );
};

export default SeenProblemUpCloseSection;

